// PRD 05 §3.2 — question card. Assembled top-to-bottom:
//   Row 1 : badges (target / gender / scope)
//   Row 2 : auto-numbered question text + QID
//   Row 3 : answer-type chip + optional 사진첨부 chip
//   Row 4 : per-type answer display
//   Row 5 : collapsible "추가 설정" accordion → ScoringBoxReadOnly
//   Row 6 : action buttons (하위문항 추가 / 미리보기 / 편집 / 삭제) — disabled in
//           this slice; handlers wire in S3/S5/S6
//
// Sub-question cards use the same component nested in a `se-sub-wrap`
// container with the 24px indent + blue left border from the prototype.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Camera,
  ChevronRight,
  Copy,
  GitBranch,
  Pencil,
  Smartphone,
  Trash2,
} from 'lucide-react';

import { AnswerDisplay } from './AnswerDisplay';
import { QuestionBadges } from './QuestionBadges';
import { ScoringBoxReadOnly } from './ScoringBoxReadOnly';
import type { Applicability, AnswerType, Question } from '../types/questionnaire-design.types';
import { DirtyBadge } from '@/components/feedback/DirtyBadge';
import { useWriteDenyGuard } from '@/shared/hooks/useWriteGuard';

const ANSWER_TYPE_KEY: Record<AnswerType, string> = {
  SHORT_ANSWER: 'enums.answerType.shortAnswer',
  MULTI_CHOICE: 'enums.answerType.multiChoice',
  YES_NO: 'enums.answerType.yesNo',
  OX: 'enums.answerType.ox',
  TEXT: 'enums.answerType.text',
  SLIDER: 'enums.answerType.slider',
  PHOTO: 'enums.answerType.photo',
  BATTERY: 'enums.answerType.battery',
};

// PRD 05 §3.5 [Q86] / §3.7 [Q88] — sub-questions hang off a parent's branch
// condition, so `[하위문항 추가]` is offered ONLY on branch-supporting main
// question types (단답형·Yes/No). Other types never branch, so they never get
// the button.
const BRANCHABLE_TYPES: ReadonlySet<AnswerType> = new Set<AnswerType>([
  'SHORT_ANSWER',
  'YES_NO',
]);

interface QuestionCardProps {
  question: Question;
  displayNum: string;
  // Sub cards render with the indented wrapper + `└` glyph + no 하위문항 button.
  isSub: boolean;
  jumpDisplayNumByQid: Map<string, string>;
  // SHCT-582 — PRD 21 §7.4 P-2. Write actions are `disabled` + 툴팁 without PRO_MGMT:WRITE, never
  // withheld: QC's re-test found a READ-only Role saw cards with no action row at all, which reads
  // as a broken screen rather than as a permission. The `data-perm` markers below have always
  // described the same rule; nothing in the real admin web acted on them until now.
  canWrite: boolean;
  // SHCT-517 / [ADM.046] — the Scope inherited from the parent item, which is what the badge shows.
  // `null` when the tree has no scope for the item (a server build older than the field); the card
  // then falls back to the question's own stored value.
  itemScope: Applicability | null;
  // Hooks for the action row. Each one is optional — when absent the button is
  // not rendered at all (a structural absence, e.g. 미리보기 on a screen that has none).
  onAddSub?: () => void;
  // SHCT-862 — opens the copy modal in its 하위문항 entry point. Omitted on sub cards.
  onCopyAsSub?: () => void;
  onPreview?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function QuestionCard({
  question,
  displayNum,
  isSub,
  jumpDisplayNumByQid,
  canWrite,
  itemScope,
  onAddSub,
  onCopyAsSub,
  onPreview,
  onEdit,
  onDelete,
}: QuestionCardProps) {
  const { t } = useTranslation('questionnaire-design');
  const { denyProps } = useWriteDenyGuard(canWrite);
  const [advancedOpen, setAdvancedOpen] = useState(true);
  // FEATURE_025 — the server decides this marker (`question.dirty`) by comparing the question with its
  // deployed copy; the browser's own list is no longer OR-ed in.
  const isDirty = question.dirty === true;

  const card = (
    <div className="card" style={{ overflow: 'hidden' }}>
      <QuestionBadges
        target={question.target}
        gender={question.gender}
        // SHCT-517 / [ADM.046] — the badge shows the scope INHERITED from the item, not
        // `question.applicability`. The server sends both, and they can disagree: `applicability` is
        // the question's own stored column, which older rows still hold from before the question
        // stopped owning a scope. Reading it here made the list contradict the editor (which reads
        // the item) and left the badge stale after the item's Scope changed, since it only refreshed
        // when the question itself was saved.
        //
        // `itemScope` falls back to the stored value for a server build older than the field, so the
        // badge degrades to the previous behaviour rather than disappearing.
        applicability={itemScope ?? question.applicability}
      />

      <div style={{ padding: '0 var(--space-6) var(--space-2)' }}>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            color: 'var(--foreground)',
          }}
        >
          {isSub ? '└ ' : ''}
          {displayNum}. {question.text}
          <span
            style={{
              fontSize: 10,
              color: 'var(--muted-foreground)',
              fontWeight: 400,
              marginLeft: 6,
            }}
          >
            {question.qid}
          </span>
          {isDirty ? (
            <DirtyBadge
              style={{ marginLeft: 6, fontSize: 10, padding: '2px 6px' }}
              title={t('card.dirtyTooltip')}
            />
          ) : null}
        </div>
      </div>

      <div style={{ padding: '0 var(--space-6) var(--space-3)' }}>
        <span className="badge badge-secondary" style={{ padding: '2px 10px' }}>
          {t(ANSWER_TYPE_KEY[question.answerType])}
        </span>
        {question.allowPhoto ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              fontSize: 'var(--font-size-xs)',
              padding: '2px 10px',
              borderRadius: 'var(--radius-sm)',
              background: '#FEF3C7',
              color: '#92400E',
              fontWeight: 'var(--font-weight-medium)',
              marginLeft: 6,
            }}
          >
            <Camera size={12} /> {t('card.photoBadge')}
          </span>
        ) : null}
      </div>

      <AnswerDisplay question={question} jumpDisplayNumByQid={jumpDisplayNumByQid} />

      <div style={{ borderTop: '1px solid var(--border)' }}>
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          aria-expanded={advancedOpen}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            width: '100%',
            padding: 'var(--space-3) var(--space-6)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 'var(--font-size-xs)',
            fontFamily: 'var(--font-family)',
            color: 'var(--muted-foreground)',
            transition: 'color 0.15s',
          }}
        >
          <ChevronRight
            size={12}
            style={{
              transition: 'transform 0.2s',
              transform: advancedOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          />{' '}
          {t('card.advancedToggle')}
        </button>
        {advancedOpen ? (
          <div style={{ padding: '0 var(--space-6) var(--space-4)' }}>
            <ScoringBoxReadOnly question={question} />
          </div>
        ) : null}
      </div>

      <div
        style={{
          padding: 'var(--space-3) var(--space-6)',
          borderTop: '1px solid var(--border)',
          background: '#fafafa',
        }}
      >
        {/* Branch action gets its own row above the standard actions so it
            never crowds / clips them in the narrow 2-column layout. */}
        {!isSub && onAddSub && BRANCHABLE_TYPES.has(question.answerType) ? (
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={onAddSub}
              data-perm="PRO_MGMT:WRITE"
              disabled={!canWrite}
              style={{ color: canWrite ? 'var(--info)' : undefined }}
              {...denyProps}
            >
              <GitBranch size={16} /> {t('card.actions.addSub')}
            </button>
            {/* SHCT-862 — PRD 05 v1.34 §3.8.1 puts `복사하여 하위문항추가` next to
                `[하위문항 추가]`: one creates an empty sub-question, the other reuses an
                existing one. Both are gated the same way — main cards only (a sub card would
                breach the 2-depth limit §3.7 sets) and only on branch-capable answer types,
                since a sub-question exists to hang off a branch. */}
            {onCopyAsSub ? (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={onCopyAsSub}
                data-perm="PRO_MGMT:WRITE"
                disabled={!canWrite}
                style={{ color: canWrite ? 'var(--info)' : undefined }}
                {...denyProps}
              >
                <Copy size={16} /> {t('card.actions.copyAsSub')}
              </button>
            ) : null}
          </div>
        ) : null}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-start',
            gap: 'var(--space-4)',
          }}
        >
          {onPreview ? (
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={onPreview}
              style={{ color: 'var(--info)' }}
            >
              <Smartphone size={16} /> {t('card.actions.preview')}
            </button>
          ) : null}
          {onEdit ? (
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={onEdit}
              data-perm="PRO_MGMT:WRITE"
              disabled={!canWrite}
              {...denyProps}
            >
              <Pencil size={16} /> {t('card.actions.edit')}
            </button>
          ) : null}
          {onDelete ? (
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={onDelete}
              data-perm="PRO_MGMT:WRITE"
              disabled={!canWrite}
              style={{ color: canWrite ? 'var(--destructive)' : undefined }}
              {...denyProps}
            >
              <Trash2 size={16} /> {t('card.actions.delete')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );

  if (isSub) {
    return (
      <div
        style={{
          marginLeft: 24,
          borderLeft: '3px solid var(--info)',
          paddingLeft: 'var(--space-4)',
          marginBottom: 'var(--space-4)',
        }}
      >
        {card}
      </div>
    );
  }

  return <div style={{ marginBottom: 'var(--space-4)' }}>{card}</div>;
}
